import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Space, Tag, Popconfirm, Spin, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { servicesAPI } from '../services/api';

const { Title } = Typography;

export default function Services() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(null);

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            setLoading(true);
            const response = await servicesAPI.getAll();
            setServices(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching services:', error);
            message.error('Failed to fetch services');
            setServices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setDeleteLoading(id);
            await servicesAPI.delete(id);
            setServices(services.filter((s) => (s.id || s._id) !== id));
            message.success('Service deleted successfully');
        } catch (error) {
            console.error('Error deleting service:', error);
            message.error('Failed to delete service');
        } finally {
            setDeleteLoading(null);
        }
    };

    const categoryLabels = {
        design: 'Design',
        structure: 'Structure',
        'slf-pbg': 'SLF & PBG',
        testing: 'Testing',
        soil: 'Soil Investigation',
    };

    const categoryColors = {
        design: 'blue',
        structure: 'green',
        'slf-pbg': 'orange',
        testing: 'purple',
        soil: 'cyan',
    };

    const columns = [
        {
            title: 'Service Name',
            dataIndex: 'title',
            key: 'title',
            width: '40%',
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => (
                <Tag color={categoryColors[category] || 'default'}>
                    {categoryLabels[category] || category}
                </Tag>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => text?.substring(0, 100) + (text?.length > 100 ? '...' : ''),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Link to={`/services/edit/${record.id || record._id}`}>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            size="small"
                        >
                            Edit
                        </Button>
                    </Link>
                    <Popconfirm
                        title="Are you sure you want to delete this service?"
                        onConfirm={() => handleDelete(record.id || record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            loading={deleteLoading === (record.id || record._id)}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading services..." />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>Services Management</Title>
                <Link to="/services/new">
                    <Button type="primary" icon={<PlusOutlined />} size="large">
                        Add New Service
                    </Button>
                </Link>
            </div>

            <Table
                columns={columns}
                dataSource={services}
                rowKey={(record) => record.id || record._id}
                pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} services`,
                }}
                locale={{
                    emptyText: 'No services found',
                }}
            />
        </div>
    );
}
